import React from "react";
import { Coins, Smile, TrendingUp, Download } from "lucide-react";
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from "recharts";
import { PageHeader, EventSelector, ProgressBar } from "@/components/common";
import { Button } from "@/components/ui/button";
import { euro } from "@/lib/format";
import { toast } from "sonner";

// Données de démonstration réalistes (page 100% statique, non liée aux données réelles)
const SOLD = 342, MAXP = 400, PARTICIPATION = 86, COST_PER = 118, EST_COST = 125, SATISFACTION = 92;

const SATISFACTION_DATA = [
  { name: "Très satisfait", value: 68, color: "#10b981" },
  { name: "Satisfait", value: 24, color: "#3b82f6" },
  { name: "Neutre", value: 6, color: "#f59e0b" },
  { name: "Insatisfait", value: 2, color: "#ef4444" },
];
const ATTENDANCE_BY_HOUR = [
  { slot: "08h", Présents: 45 }, { slot: "09h", Présents: 168 }, { slot: "10h", Présents: 290 },
  { slot: "11h", Présents: 312 }, { slot: "12h", Présents: 205 }, { slot: "13h", Présents: 148 },
  { slot: "14h", Présents: 301 }, { slot: "15h", Présents: 318 }, { slot: "16h", Présents: 264 },
  { slot: "17h", Présents: 172 }, { slot: "18h", Présents: 88 },
];
const ACQUISITION = [
  { name: "Réseaux sociaux", value: 128, color: "#8b5cf6" }, { name: "Email / Newsletter", value: 96, color: "#3b82f6" },
  { name: "Bouche-à-oreille", value: 61, color: "#10b981" }, { name: "Site web", value: 34, color: "#f59e0b" },
  { name: "Partenaires", value: 23, color: "#ec4899" },
];
const RADAR_DATA = [
  { metric: "Participation", value: PARTICIPATION }, { metric: "Satisfaction", value: SATISFACTION },
  { metric: "Confirmations", value: 93 }, { metric: "Respect budget", value: 89 }, { metric: "ROI", value: 67 },
];
const GLOBAL_SCORE = Math.round(RADAR_DATA.reduce((a, r) => a + r.value, 0) / RADAR_DATA.length);

function StatCard({ label, value, sub, subColor = "text-emerald-600", icon: Icon }) {
  return (
    <div className="bg-card rounded-xl border border-border p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-muted-foreground">{label}</p>
          <p className="font-display text-3xl font-bold mt-1">{value}</p>
          {sub && <p className={`text-sm mt-1 font-medium ${subColor}`}>{sub}</p>}
        </div>
        {Icon && <Icon className={`h-5 w-5 ${subColor}`} />}
      </div>
    </div>
  );
}

export default function Statistics() {
  return (
    <>
      <PageHeader title="Statistiques" subtitle="Analysez les performances de votre événement"
        actions={<div className="flex gap-2"><EventSelector /><Button variant="outline" onClick={() => toast.success("Rapport statistiques exporté")} data-testid="export-stats"><Download className="h-4 w-4 mr-1" />Exporter</Button></div>} />

      <div className="mb-6 text-xs text-muted-foreground bg-slate-50 border border-slate-200 rounded-lg px-3 py-2" data-testid="stats-demo-notice">
        📊 Données de démonstration — cette page présente un exemple de tableau de bord statistique.
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-card rounded-xl border border-border p-5">
          <p className="text-sm text-muted-foreground">Billets vendus</p>
          <p className="font-display text-3xl font-bold mt-1">{SOLD}</p>
          <p className="text-xs text-muted-foreground mb-2">sur {MAXP} places</p>
          <ProgressBar pct={PARTICIPATION} />
        </div>
        <StatCard label="Taux de participation" value={`${PARTICIPATION}%`} sub="vs objectif" icon={TrendingUp} />
        <StatCard label="Coût par invité" value={euro(COST_PER)} sub={`Estimé: ${euro(EST_COST)}`} subColor="text-amber-600" icon={Coins} />
        <StatCard label="Taux de satisfaction" value={`${SATISFACTION}%`} sub="Excellent" icon={Smile} />
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mb-6">
        <div className="bg-card rounded-xl border border-border p-5">
          <h3 className="font-display font-semibold mb-1">Affluence par créneau horaire</h3>
          <p className="text-xs text-muted-foreground mb-3">Participants présents sur site au fil de la journée</p>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={ATTENDANCE_BY_HOUR}><CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" /><XAxis dataKey="slot" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} /><Tooltip /><Bar dataKey="Présents" fill="#0ea5e9" radius={[4, 4, 0, 0]} /></BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-card rounded-xl border border-border p-5">
          <h3 className="font-display font-semibold mb-4">Satisfaction participants</h3>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart><Pie data={SATISFACTION_DATA} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={95} label={({ value }) => `${value}%`}>{SATISFACTION_DATA.map((s, i) => <Cell key={i} fill={s.color} />)}</Pie><Tooltip formatter={(v) => `${v}%`} /><Legend /></PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mb-6">
        <div className="bg-card rounded-xl border border-border p-5">
          <h3 className="font-display font-semibold mb-1">Canaux d'acquisition des inscrits</h3>
          <p className="text-xs text-muted-foreground mb-3">Comment les participants ont connu l'événement</p>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart><Pie data={ACQUISITION} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={95} label={({ name }) => name}>{ACQUISITION.map((s, i) => <Cell key={i} fill={s.color} />)}</Pie><Tooltip /><Legend /></PieChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-card rounded-xl border border-border p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-semibold">Radar de performance</h3>
            <div className="text-right"><p className="text-xs text-muted-foreground uppercase tracking-wide">Score global</p><p className="font-display text-2xl font-bold text-emerald-600" data-testid="global-score">{GLOBAL_SCORE}<span className="text-sm font-normal text-muted-foreground">/100</span></p></div>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <RadarChart data={RADAR_DATA} outerRadius="72%">
              <PolarGrid stroke="#e2e8f0" /><PolarAngleAxis dataKey="metric" tick={{ fontSize: 11, fill: "#475569" }} /><PolarRadiusAxis domain={[0, 100]} tick={{ fontSize: 9, fill: "#94a3b8" }} angle={90} />
              <Radar name="Performance" dataKey="value" stroke="#6366f1" fill="#8b5cf6" fillOpacity={0.45} strokeWidth={2} /><Tooltip formatter={(v) => `${v}/100`} />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </>
  );
}
